/**
 * Office Management Page - Modern Design
 */

import { useState } from 'react'
import { Search, Building2, Edit, Trash2, Plus, X, UserCheck } from 'lucide-react'
import {
  useOffices,
  useCreateOffice,
  useUpdateOffice,
  useDeleteOffice,
} from '@/hooks/useUsers'
import { useLawyers } from '@/hooks/useCases'
import { toast } from '@/lib/toast'
import type { OfficeCreate } from '@/types/user'

export function OfficeManagement() {
  const [search, setSearch] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editOfficeId, setEditOfficeId] = useState<number | null>(null)
  const [deleteOfficeId, setDeleteOfficeId] = useState<number | null>(null)
  const [assignHeadOfficeId, setAssignHeadOfficeId] = useState<number | null>(null)
  const [selectedHeadId, setSelectedHeadId] = useState<string>('')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 20

  // Fetch data
  const { data: offices, isLoading, error, refetch } = useOffices()
  const { data: lawyers } = useLawyers()
  const { data: officeLawyers } = useLawyers(assignHeadOfficeId ?? undefined)

  // Mutations
  const createOffice = useCreateOffice()
  const updateOffice = useUpdateOffice()
  const deleteOffice = useDeleteOffice()

  // Form state
  const [formData, setFormData] = useState<OfficeCreate>({
    office: '',
    code: '',
    office_head_id: null,
  })

  // Filter offices
  const filteredOffices = offices?.filter(office =>
    search === '' ||
    office.office.toLowerCase().includes(search.toLowerCase()) ||
    office.code.toLowerCase().includes(search.toLowerCase())
  )

  // Calculate pagination
  const totalPages = Math.ceil((filteredOffices?.length || 0) / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedOffices = filteredOffices?.slice(startIndex, endIndex)

  // Reset to page 1 when search changes
  const handleSearchChange = (value: string) => {
    setSearch(value)
    setCurrentPage(1)
  }

  // Handle edit
  const handleEdit = (officeId: number) => {
    const office = offices?.find(o => o.id === officeId)
    if (!office) return

    setFormData({
      office: office.office,
      code: office.code,
      office_head_id: office.office_head_id || null,
    })
    setEditOfficeId(officeId)
  }

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      if (editOfficeId) {
        await updateOffice.mutateAsync({ id: editOfficeId, data: formData })
        toast.success('Office updated successfully')
        setEditOfficeId(null)
      } else {
        await createOffice.mutateAsync(formData)
        toast.success('Office created successfully')
        setShowCreateModal(false)
      }
      resetForm()
      refetch()
    } catch (error: any) {
      toast.error(editOfficeId ? 'Failed to update office' : 'Failed to create office', error.message)
    }
  }

  // Handle delete
  const handleDelete = async () => {
    if (deleteOfficeId === null) return

    try {
      await deleteOffice.mutateAsync(deleteOfficeId)
      toast.success('Office deleted successfully')
      setDeleteOfficeId(null)
      refetch()
    } catch (error: any) {
      toast.error('Failed to delete office', error.message)
    }
  }

  // Handle assign head
  const handleOpenAssignHead = (officeId: number) => {
    const office = offices?.find(o => o.id === officeId)
    setSelectedHeadId(office?.office_head_id || '')
    setAssignHeadOfficeId(officeId)
  }

  const handleAssignHead = async () => {
    if (assignHeadOfficeId === null) return

    try {
      await updateOffice.mutateAsync({
        id: assignHeadOfficeId,
        data: { office_head_id: selectedHeadId || null },
      })
      toast.success(selectedHeadId ? 'Office head assigned successfully' : 'Office head removed')
      setAssignHeadOfficeId(null)
      setSelectedHeadId('')
      refetch()
    } catch (error: any) {
      toast.error('Failed to assign office head', error.message)
    }
  }

  const resetForm = () => {
    setFormData({
      office: '',
      code: '',
      office_head_id: null,
    })
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Offices</h2>
          <p className="text-gray-600 mb-6">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary flex items-center gap-3">
            <Building2 className="h-8 w-8" />
            Office Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage PAO office locations
          </p>
        </div>
        <button
          onClick={() => {
            resetForm()
            setShowCreateModal(true)
          }}
          className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-sm font-medium"
        >
          <Plus className="w-5 h-5" />
          Add Office
        </button>
      </div>

      {/* Search Card */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by office name or code..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* Results Summary */}
      {filteredOffices && filteredOffices.length > 0 && (
        <div className="text-sm text-gray-600">
          Showing <span className="font-semibold text-gray-900">{startIndex + 1}</span> to{' '}
          <span className="font-semibold text-gray-900">{Math.min(endIndex, filteredOffices.length)}</span> of{' '}
          <span className="font-semibold text-gray-900">{filteredOffices.length}</span> offices
          {search && ` (filtered from ${offices?.length || 0} total)`}
        </div>
      )}

      {/* Table Card */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent mb-4"></div>
            <p className="text-gray-600">Loading offices...</p>
          </div>
        ) : filteredOffices?.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No offices found</h3>
            <p className="text-gray-600 mb-6">
              {search ? 'Try adjusting your search' : 'Create your first office to get started'}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg transition-colors"
            >
              Create First Office
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Office Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Office Head
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedOffices?.map((office) => (
                  <tr key={office.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900">{office.office}</span>
                    </td>
                    <td className="px-6 py-4">
                      <code className="px-2 py-1 bg-gray-100 rounded text-sm font-medium">
                        {office.code}
                      </code>
                    </td>
                    <td className="px-6 py-4">
                      {office.office_head_name ? (
                        <span className="text-gray-900">{office.office_head_name}</span>
                      ) : (
                        <span className="text-gray-400 italic">Not assigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenAssignHead(office.id)}
                          className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 font-medium transition-colors"
                          title="Assign Office Head"
                        >
                          <UserCheck className="w-4 h-4" />
                          <span className="text-sm">Assign Head</span>
                        </button>

                        <button
                          onClick={() => handleEdit(office.id)}
                          className="inline-flex items-center gap-1 text-primary hover:text-primary/80 font-medium transition-colors"
                          title="Edit Office"
                        >
                          <Edit className="w-4 h-4" />
                          <span className="text-sm">Edit</span>
                        </button>

                        <button
                          onClick={() => setDeleteOfficeId(office.id)}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 font-medium transition-colors"
                          title="Delete Office"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="text-sm">Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editOfficeId) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">
                {editOfficeId ? 'Edit Office' : 'Create New Office'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setEditOfficeId(null)
                  resetForm()
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Office Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.office}
                  onChange={(e) => setFormData({ ...formData, office: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., Public Attorney's Office - Main"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Office Code *
                </label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., PAO-MAIN"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Office Head
                </label>
                <select
                  value={formData.office_head_id || ''}
                  onChange={(e) => setFormData({ ...formData, office_head_id: e.target.value || null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">-- None --</option>
                  {lawyers?.map((lawyer) => (
                    <option key={lawyer.userid} value={lawyer.userid}>
                      {lawyer.fullname}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                >
                  {editOfficeId ? 'Update Office' : 'Create Office'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    setEditOfficeId(null)
                    resetForm()
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteOfficeId !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Office</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this office? Users assigned to this office will be unassigned.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors font-medium"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteOfficeId(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Office Head Modal */}
      {assignHeadOfficeId !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-amber-600" />
                Assign Office Head
              </h3>
              <button
                onClick={() => {
                  setAssignHeadOfficeId(null)
                  setSelectedHeadId('')
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Office</p>
                <p className="font-semibold text-gray-900">
                  {offices?.find(o => o.id === assignHeadOfficeId)?.office}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Office Head
                </label>
                <select
                  value={selectedHeadId}
                  onChange={(e) => setSelectedHeadId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">-- None (Remove Head) --</option>
                  {officeLawyers?.map((lawyer) => (
                    <option key={lawyer.userid} value={lawyer.userid}>
                      {lawyer.fullname}
                    </option>
                  ))}
                </select>
                {officeLawyers?.length === 0 && (
                  <p className="text-sm text-amber-600 mt-1">No lawyers assigned to this office.</p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleAssignHead}
                  className="flex-1 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                >
                  {selectedHeadId ? 'Assign Head' : 'Remove Head'}
                </button>
                <button
                  onClick={() => {
                    setAssignHeadOfficeId(null)
                    setSelectedHeadId('')
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OfficeManagement
